<script setup>
import { ref, watch } from "vue";
import { useDocs } from "../../composables/useDocs.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import Spinner from "../ui/Spinner.vue";

const props = defineProps({
  isActive: { type: Boolean, default: false }, // panel is mounted app-wide (v-show); only fetch once it's actually opened
});
const emit = defineEmits(["close"]);

// Shares the exact same fetched list as the Docs page — same singleton-only
// document source, so both stay in sync and a refresh (e.g. after an upload)
// updates whichever is currently visible.
const { docs: allDocs, isLoading, isLoadingMore, hasMore, load, loadMore } = useDocs();

watch(() => props.isActive, (active) => { if (active) load(); }, { immediate: true });

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore());
</script>

<template>
  <div class="flex flex-col h-full w-72 bg-slate-100 dark:bg-slate-900 overflow-hidden">

    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shrink-0">
      <span class="text-[15px] font-semibold text-slate-800 dark:text-slate-100">All Documents</span>
      <button
        class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors shrink-0"
        @click="emit('close')"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-10"><Spinner size="22px" /></div>

    <!-- Doc list -->
    <div v-else class="flex-1 overflow-y-auto py-3 px-2">

      <div
        v-for="doc in allDocs"
        :key="doc.id"
        class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700/60 cursor-pointer group transition-colors"
      >
        <svg class="shrink-0 text-slate-500 dark:text-slate-400" width="20" height="22" viewBox="0 0 24 26" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
        <span class="flex-1 text-[14px] font-medium text-slate-800 dark:text-slate-100 truncate min-w-0">{{ doc.name ?? "Untitled" }}</span>
        <button class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0 transition-colors" @click.stop>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      </div>

      <p v-if="!allDocs.length" class="text-xs text-slate-400 dark:text-slate-500 text-center py-8 px-4 m-0">No documents found</p>

      <!-- Infinite scroll sentinel -->
      <div ref="sentinelEl" class="h-px" />
      <div v-if="isLoadingMore" class="flex justify-center py-3"><Spinner size="16px" /></div>
    </div>
  </div>
</template>
